import React, { useState, useEffect } from 'react';
import { ArrowLeft, MapPin, Loader2, Navigation, List as ListIcon, Layers } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Button } from '../components/ui/button';
import BottomNav from '../components/BottomNav';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Fix default marker icon issue with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icon
const createCustomIcon = (color = '#E07A5F') => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="
      background-color: ${color};
      width: 32px;
      height: 32px;
      border-radius: 50% 50% 50% 0;
      transform: rotate(-45deg);
      border: 3px solid white;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    "><div style="
      transform: rotate(45deg);
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 14px;
    ">●</div></div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });
};

// Component to recenter map
const RecenterMap = ({ lat, lng }) => {
  const map = useMap();
  useEffect(() => {
    if (lat && lng) {
      map.setView([lat, lng], 13);
    }
  }, [lat, lng, map]);
  return null;
};

// Component to handle user location
const LocationMarker = ({ onLocationFound }) => {
  const map = useMap();
  
  useEffect(() => {
    map.locate({ setView: false, maxZoom: 13 });
    
    map.on('locationfound', (e) => {
      onLocationFound(e.latlng);
    });
  }, [map, onLocationFound]);
  
  return null;
};

const MapView = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [mapStyle, setMapStyle] = useState('default');
  
  // Get center from URL params or default to Brussels, Belgium
  const centerLat = parseFloat(searchParams.get('lat')) || 50.8503;
  const centerLng = parseFloat(searchParams.get('lng')) || 4.3517;

  useEffect(() => {
    fetchActivities();
  }, []);

  const fetchActivities = async () => {
    try {
      const response = await axios.get(`${API}/api/activities?limit=50`);
      setActivities(response.data.activities);
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLocationFound = (latlng) => {
    setUserLocation(latlng);
  };

  const markers = activities.filter(a => a.latitude && a.longitude);

  const priceColors = {
    free: '#10B981',
    budget: '#81B29A',
    'mid-range': '#F59E0B',
    premium: '#E07A5F'
  };

  const tileLayers = {
    default: {
      url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
    },
    light: {
      url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
    },
    dark: {
      url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] pb-24">
      {/* Header */}
      <header className="sticky top-0 z-[1000] glass border-b border-[#2C302E]/5">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="w-10 h-10 rounded-full bg-white border border-[#2C302E]/10 flex items-center justify-center"
              data-testid="map-back-btn"
            >
              <ArrowLeft className="w-5 h-5 text-[#2C302E]" />
            </button>
            <h1 className="text-xl font-semibold text-[#2C302E] font-['Outfit']">{t('map_title')}</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setMapStyle(prev => prev === 'default' ? 'light' : prev === 'light' ? 'dark' : 'default')}
              className="rounded-full"
            >
              <Layers className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/discover')}
              className="rounded-full"
            >
              <ListIcon className="w-4 h-4 mr-2" />
              {t('map_list_view')}
            </Button>
          </div>
        </div>
      </header>

      {/* Map Container */}
      <div className="relative h-[55vh] min-h-[350px]">
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-[#E8E6E3]">
            <Loader2 className="w-8 h-8 animate-spin text-[#E07A5F]" />
          </div>
        ) : (
          <MapContainer
            center={[centerLat, centerLng]}
            zoom={10}
            style={{ height: '100%', width: '100%' }}
            className="z-0"
          >
            <TileLayer
              attribution={tileLayers[mapStyle].attribution}
              url={tileLayers[mapStyle].url}
            />
            
            <LocationMarker onLocationFound={handleLocationFound} />
            
            {userLocation && (
              <RecenterMap lat={userLocation.lat} lng={userLocation.lng} />
            )}
            
            {/* User location marker */}
            {userLocation && (
              <Marker 
                position={[userLocation.lat, userLocation.lng]}
                icon={L.divIcon({
                  className: 'user-marker',
                  html: `<div style="
                    background-color: #3B82F6;
                    width: 20px;
                    height: 20px;
                    border-radius: 50%;
                    border: 3px solid white;
                    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.5);
                  "></div>`,
                  iconSize: [20, 20],
                  iconAnchor: [10, 10],
                })}
              >
                <Popup>
                  <div className="text-center">
                    <p className="font-medium">Your Location</p>
                  </div>
                </Popup>
              </Marker>
            )}
            
            {/* Activity markers */}
            {markers.map((activity) => (
              <Marker
                key={activity.id}
                position={[activity.latitude, activity.longitude]}
                icon={createCustomIcon(priceColors[activity.price_range] || '#E07A5F')}
                eventHandlers={{
                  click: () => setSelectedActivity(activity)
                }}
              >
                <Popup>
                  <div className="w-48">
                    <img 
                      src={activity.images?.[0] || 'https://via.placeholder.com/200'} 
                      alt={activity.title}
                      className="w-full h-24 object-cover rounded-lg mb-2"
                    />
                    <h3 className="font-semibold text-[#2C302E] text-sm">{activity.title}</h3>
                    <p className="text-xs text-[#666B67] flex items-center gap-1 mt-1">
                      <MapPin className="w-3 h-3" />
                      {activity.city}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        activity.price_range === 'free' 
                          ? 'bg-emerald-100 text-emerald-700'
                          : activity.price_range === 'premium'
                          ? 'bg-[#E07A5F]/10 text-[#E07A5F]'
                          : 'bg-[#81B29A]/10 text-[#81B29A]'
                      }`}>
                        {activity.price_range === 'free' ? 'Gratis' : 
                         activity.price_range === 'premium' ? 'Premium' : 
                         activity.price_range}
                      </span>
                    </div>
                    <button
                      onClick={() => navigate(`/activity/${activity.id}`)}
                      className="w-full mt-2 text-xs text-[#E07A5F] font-medium hover:underline"
                    >
                      {t('card_view_details')} →
                    </button>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        )}

        {/* Center on user location button */}
        {userLocation && (
          <button
            onClick={() => {}}
            className="absolute bottom-4 right-4 z-[1000] w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:shadow-xl transition-shadow"
            data-testid="center-location-btn"
          >
            <Navigation className="w-5 h-5 text-[#E07A5F]" />
          </button>
        )}
      </div>

      {/* Activity List */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        <h2 className="text-lg font-semibold text-[#2C302E] mb-4 font-['Outfit']">
          {markers.length} {t('map_activities_nearby')}
        </h2>

        <div className="space-y-3">
          {markers.slice(0, 5).map((activity) => (
            <button
              key={activity.id}
              onClick={() => navigate(`/activity/${activity.id}`)}
              className={`w-full bg-white rounded-2xl p-4 flex items-center gap-4 text-left hover:shadow-md transition-shadow ${
                selectedActivity?.id === activity.id ? 'ring-2 ring-[#E07A5F]' : ''
              }`}
              data-testid={`map-activity-${activity.id}`}
            >
              <img 
                src={activity.images?.[0] || 'https://via.placeholder.com/80'} 
                alt={activity.title}
                className="w-16 h-16 rounded-xl object-cover flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-[#2C302E] truncate">{activity.title}</h3>
                <p className="text-sm text-[#666B67] flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {activity.city}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    activity.price_range === 'free' 
                      ? 'bg-emerald-100 text-emerald-700'
                      : activity.price_range === 'premium'
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-[#81B29A]/10 text-[#81B29A]'
                  }`}>
                    {activity.price_range === 'free' ? 'Gratis' : 
                     activity.price_range === 'premium' ? 'Premium' : 
                     activity.price_range}
                  </span>
                  <span className="text-xs text-[#666B67]">
                    {activity.indoor_outdoor === 'indoor' ? 'Binnen' : 
                     activity.indoor_outdoor === 'outdoor' ? 'Buiten' : 'Beide'}
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>

        {markers.length > 5 && (
          <div className="mt-4 text-center">
            <Button
              variant="outline"
              onClick={() => navigate('/discover?tab=near_me')}
              className="rounded-full"
            >
              {t('map_view_all')} {markers.length}
            </Button>
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default MapView;
